import { Static, Type } from '@sinclair/typebox';
import { ProviderSchema } from '../../common/provider.schema';
import { AttachmentSchema } from '../../shared/attachment/attachment.schema';
import { reactionCounterSchema } from '../../shared/reaction/reaction-counter.schema';
import { MinimalUser, minimalUserSchema } from '../../shared/user/user.schema';
import { StringEnum } from '../../utils/typebox';

export enum MessageEventType {
  UNKNOWN,
  NAME_UPDATE,
  DESCRIPTION_UPDATE,
  PARTICIPANT_ADD,
  PARTICIPANT_REMOVE,
  MESSAGE_PIN,
  PERMISSION_UPDATE,
  PARTICIPANT_UPDATE,
  REACTION,
  CALL_MISSED,
  CALL_STARTED,
  CALL_ENDED,
  CALL_REJECTED,
  SCHEDULED_CALL_CREATED,
  SCHEDULED_CALL_CANCELLED,
  SCHEDULED_CALL_STARTED,
  ANNOUNCEMENT,
  CONTENT_MENTION,
  // PAYMENT_CANCELLED,
}

export const messageEventTypeSchema = Type.Enum(MessageEventType, {
  description: `The type of message event.
    0 : Unknown (Not implemented)
    1 : Chat name update
    2 : Chat description update
    3 : New participant added to the chat group
    4 : Participant kicked or left the chat group
    5 : A message is pinned
    6 : Permissions have been updated
    7 : Participant was promoted or demoted
    8 : Reaction
    9 : Call Missed
    10 : Call Started
    11 : Call Ended
    12 : Call Rejected
    13 : Scheduled Call Created
    14 : Scheduled Call Cancelled
    15 : Scheduled Call Started
    16 : Announcement
    17 : Content mention
    `,
});

export const quotedMessageSchema = Type.Object(
  {
    object: Type.Literal('QuotedMessage'),
    id: Type.String({ description: 'The unique identifier of the message for the provider.' }),
    text: Type.Optional(Type.String({ description: 'The text content of the message.' })),
    specifics: Type.Optional(Type.Any()),
    attachments: Type.Array(AttachmentSchema, { description: 'List of message attachments.' }),
    sender: Type.Optional(Type.Object(minimalUserSchema, { description: 'The user who sent the message.' })),
  },
  { description: 'The quoted message.' },
);

export const forwardedMessageSchema = Type.Composite(
  [Type.Omit(quotedMessageSchema, ['object', 'id']), Type.Object({ object: Type.Literal('ForwardedMessage') })],
  {
    description: `Contains the original message if this message was forwarded from another chat, depending on provider support.
    Some providers preserve the original sender and content, treating it as if it were sent by the account owner.
    Others wrap the forwarded message within a new message (similar to a quote or attachment), in which case the original message appears here.`,
  },
);

export const HyperlinkSchema = Type.Object(
  {
    url: Type.String({ description: 'The URL the link points to.' }),
    starts_at: Type.Number({ description: 'The index in the body where the link starts.' }),
    length: Type.Number({
      description: 'The number of characters in the body that make up the link, starting at the `starts_at` index.',
    }),
  },
  { description: 'The parameters of an external hyperlink contained in the body.' },
);

export const MentionSchema = Type.Object(
  {
    user_id: Type.Optional(
      Type.String({
        description: `ID of the mentioned user, which can be used to mention them when sending a message. Absent when the provider reports the mention without an identifier, or when the mention addresses the whole chat rather than a user.`,
      }),
    ),
    url: Type.Optional(
      Type.String({
        description: `Public profile URL of the mentioned user. Absent when the provider exposes no public profile for them.`,
      }),
    ),
    start: Type.Number({ description: 'The index in the body where the mention starts.' }),
    length: Type.Number({
      description: 'The number of characters in the body that make up the mention, starting at the `start` index.',
    }),
  },
  { description: 'The parameters of a user mention contained in the body.' },
);

const participantRemoveMetadataSchema = Type.Object(
  {
    user_id: Type.Optional(Type.String({ description: 'ID of the participant who left or was removed from the group.' })),
    user_name: Type.Optional(Type.String({ description: 'Name of the participant who left or was removed from the group.' })),
    reason: StringEnum(['left', 'kicked', 'unknown'], {
      description: 'Specifies whether the participant left voluntarily or was removed by an admin.',
    }),
  },
  { title: 'Participant Removed From Chat Group' },
);

const participantRoleUpdateMetadataSchema = Type.Object(
  {
    user_id: Type.String({ description: 'ID of the participant whose role was updated.' }),
    reason: StringEnum(['promoted', 'demoted', 'unknown'], {
      description: 'The type of role change applied to the participant.',
    }),
  },
  { title: 'Participant Role Updated' },
);

const participantJoinMetadataSchema = Type.Object(
  {
    link: Type.Optional(Type.String({ description: 'The invitation link used to join the group.' })),
    user_id: Type.String({ description: 'ID of the participant who joined the group.' }),
  },
  { title: 'New Participant Joined the Chat Group' },
);

const messagePinMetadataSchema = Type.Object(
  {
    message_id: Type.Optional(Type.String({ description: 'ID of the message that was pinned or unpinned.' })),
    is_pin: Type.Boolean({ description: 'Indicates whether the message is pinned (true) or unpinned (false).' }),
    duration: Type.Optional(Type.Number({ description: 'Duration for which the message remains pinned, if applicable.' })),
  },
  { title: 'Message Pin Status' },
);

const callStartedMetadataSchema = Type.Object(
  {
    call_id: Type.Optional(Type.String({ description: 'Unique identifier of the call.' })),
    is_video: Type.Boolean({ description: 'Indicates whether the call is a video call.' }),
    link: Type.Optional(Type.String({ description: 'The link used to join the call.' })),
    start: Type.Optional(Type.String({ description: 'Start date and time of the call in ISO 8601 format.' })),
  },
  {
    title: 'Call Started',
  },
);

const callEndedMetadataSchema = Type.Object(
  {
    call_id: Type.Optional(Type.String({ description: 'Unique identifier of the call.' })),
    is_video: Type.Boolean({ description: 'Indicates whether the call is a video call.' }),
    link: Type.Optional(Type.String({ description: 'The link used to join the call.' })),
    start: Type.Optional(Type.String({ description: 'Start date and time of the call in ISO format.' })),
    end: Type.Optional(Type.String({ description: 'End date and time of the call in ISO format.' })),
    duration: Type.Optional(Type.Number({ description: 'Duration of the call in seconds (if available).' })),
  },
  {
    title: 'Call Ended',
  },
);

const callRejectedMetadataSchema = Type.Object(
  {
    call_id: Type.Optional(Type.String({ description: 'Unique identifier of the call.' })),
    is_video: Type.Boolean({ description: 'Indicates whether the call is a video call.' }),
    link: Type.Optional(Type.String({ description: 'The link used to join the call.' })),
    start: Type.Optional(Type.String({ description: 'Start date and time of the call in ISO format.' })),
    end: Type.Optional(Type.String({ description: 'End date and time of the call in ISO format.' })),
    duration: Type.Optional(Type.Number({ description: 'Duration of the call in seconds (if applicable).' })),
  },
  {
    title: 'Call Rejected',
  },
);

const scheduleCallCreatedStatusMetadataSchema = Type.Object(
  {
    call_id: Type.Optional(Type.String({ description: 'Unique identifier of the call.' })),
    is_video: Type.Boolean({ description: 'Indicates whether the call is a video call.' }),
    link: Type.Optional(Type.String({ description: 'The link used to join the call.' })),
    start: Type.String({ description: 'Expected start date and time of the call in ISO format.' }),
    end: Type.Optional(Type.String({ description: 'Expected end date and time of the call in ISO format.' })),
    duration: Type.Optional(Type.Number({ description: 'Expected call duration in seconds.' })),
  },
  {
    title: 'Scheduled Call Created',
  },
);

const scheduleCallCancelledStatusMetadataSchema = Type.Object(
  {
    call_id: Type.Optional(Type.String({ description: 'Unique identifier of the call.' })),
    is_video: Type.Boolean({ description: 'Indicates whether the call is a video call.' }),
    link: Type.Optional(Type.String({ description: 'The link used to join the call.' })),
    start: Type.String({ description: 'Expected start date and time of the call in ISO format.' }),
    end: Type.Optional(Type.String({ description: 'Expected end date and time of the call in ISO format.' })),
    duration: Type.Optional(Type.Number({ description: 'Expected duration of the call in seconds' })),
  },
  {
    title: 'Scheduled Call Cancelled',
  },
);

const scheduleCallStartedStatusMetadataSchema = Type.Object(
  {
    call_id: Type.Optional(Type.String({ description: 'Unique identifier of the call.' })),
    is_video: Type.Boolean({ description: 'Indicates whether the call is a video call.' }),
    link: Type.Optional(Type.String({ description: 'The link used to join the call.' })),
    start: Type.String({ description: 'Start date and time of the call in ISO format.' }),
    end: Type.Optional(Type.String({ description: 'Expected end date and time of the call in ISO format.' })),
    duration: Type.Optional(Type.Number({ description: 'Duration of the call in seconds' })),
  },
  {
    title: 'Scheduled Call Started',
  },
);

// const paymentCancelledMetadataSchema = Type.Object({
//   date: Type.String({ description: 'The date when the payment was cancelled in Iso format.' }),
//   amount: Type.Number({ description: 'The amount that was cancelled.' }),
//   currency: Type.String({ description: 'The currency of the cancelled payment.' }),
// });

const titleUpdateMetadataSchema = Type.Object(
  {
    title: Type.String({ description: 'The updated title.' }),
  },
  { title: 'Chat Name Updated' },
);

const descriptionUpdateMetadataSchema = Type.Object(
  {
    description: Type.Optional(Type.String({ description: 'The updated description.' })),
  },
  { title: 'Chat Description Updated' },
);

const subjectUpdateMetadataSchema = Type.Object(
  {
    subject: Type.Optional(Type.String({ description: 'The updated subject.' })),
  },
  { title: 'Chat Subject Updated' },
);

const reactionMetadataSchema = Type.Object(
  {
    reaction: Type.String({ description: 'The reaction added or removed.' }),
    reason: StringEnum(['added', 'removed'], { description: 'Specifies whether the reaction was added or removed.' }),
    message_id: Type.String({ description: 'ID of the message to which the reaction was added or removed.' }),
  },
  { title: 'Reaction' },
);

export const contentRelationSchema = Type.Object(
  {
    type: StringEnum(['reply_to', 'mentioned_in'], {
      description:
        'How this message relates to a piece of shared content: a normal reply to it, or a provider-generated notice that the current user was mentioned in it.',
    }),
    attachment_id: Type.Optional(
      Type.String({
        description:
          "The id of this message's shared-content attachment describing the related content, when the provider still makes it available. Absent once the content is no longer retrievable (e.g. an expired story) but the relation is still known.",
      }),
    ),
  },
  { title: 'Content Relation' },
);

const metadataSchema = Type.Union([
  participantRemoveMetadataSchema,
  titleUpdateMetadataSchema,
  descriptionUpdateMetadataSchema,
  callRejectedMetadataSchema,
  callEndedMetadataSchema,
  callStartedMetadataSchema,
  scheduleCallCreatedStatusMetadataSchema,
  scheduleCallCancelledStatusMetadataSchema,
  scheduleCallStartedStatusMetadataSchema,
  messagePinMetadataSchema,
  participantJoinMetadataSchema,
  participantRoleUpdateMetadataSchema,
  subjectUpdateMetadataSchema,
  // paymentCancelledMetadataSchema,
  reactionMetadataSchema,
]);

export const messageSchema = Type.Object({
  object: Type.Literal('Message'),
  id: Type.String({ description: 'The unique identifier of the message for the provider.' }),
  text: Type.Optional(Type.String({ description: 'The text content of the message.' })),
  sender_id: Type.String({ description: 'The ID of the user who sent the message.' }),
  chat_id: Type.String({ description: 'The ID of the chat where the message was sent.' }),
  timestamp: Type.String({ description: 'The timestamp of the message. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).' }),
  is_sender: Type.Boolean({ description: 'Is the current user the sender of the message.' }),
  is_hidden: Type.Boolean({
    description:
      'Is the message hidden. Hidden messages are usually not displayed in the chat but can be as last message is chats list.',
  }),
  is_seen: Type.Boolean({ description: 'Is the message seen by other participants.' }),
  is_delivered: Type.Boolean({ description: 'Is the message delivered to the other participants.' }),
  is_deleted: Type.Boolean({ description: 'Is the message deleted. Some providers show the message as deleted in the chat.' }),
  is_edited: Type.Boolean({
    description: 'Is the message edited. Some providers adds an "edited" mark on the message in the chat.',
  }),
  is_pinned: Type.Boolean({
    description: 'Is the message pinned. Pinned messages are usually displayed at the top of the chat.',
  }),
  is_event: Type.Boolean({
    description:
      'Is the message an event. Events are special messages that are not displayed in the chat but can be used to trigger actions.',
  }),
  is_mentionned: Type.Boolean({
    description: 'Is the message mentionning the current user. Usually to notify.',
  }),
  view_mode: Type.Optional(
    StringEnum(['once', 'replayable'], {
      description: `How the ephemeral content of this message may be viewed: \`once\` for a single opening, \`replayable\` when the sender allowed it to be opened again.
    Absent when the message is not ephemeral at all - a provider that does not expose the notion, or content the sender chose to keep in the conversation.
    Depending on the provider the media may never be delivered: WhatsApp only ever serves it to the phone that owns the account, in which case the attachment is reported with \`is_unavailable\` and cannot be downloaded.`,
    }),
  ),
  event_type: Type.Optional(messageEventTypeSchema),
  event_metadata: Type.Optional(metadataSchema),
  content_relation: Type.Optional(contentRelationSchema),
  reactions_counter: reactionCounterSchema,
  sender: Type.Optional(Type.Object(minimalUserSchema, { description: 'The user who sent the message.' })),
  provider: ProviderSchema,
  attachments: Type.Array(AttachmentSchema, { description: 'List of message attachments.' }),
  hyperlinks: Type.Array(HyperlinkSchema, { description: 'List of message hyperlinks.' }),
  mentions: Type.Array(MentionSchema, {
    description: 'List of the users mentioned in the message body. A mention the provider delivered as plain text is not listed.',
  }),
  quoted: Type.Optional(quotedMessageSchema),
  forwarded: Type.Optional(forwardedMessageSchema),
  specifics: Type.Optional(Type.Any()),
});

export const messagePreviewSchema = Type.Object({
  object: Type.Literal('MessagePreview'),
  id: Type.Optional(Type.String({ description: 'The ID of the message.' })),
  text: Type.String({ description: 'The text content of the message.' }),
  sender_display_name: Type.Optional(Type.String({ description: 'The display name of the sender of the message.' })),
  is_sender: Type.Optional(Type.Boolean({ description: 'Is the message sent by the current user.' })),
});

export type Message<Specifics = unknown, SenderSpecifics = unknown> = Omit<
  Static<typeof messageSchema>,
  'specifics' | 'sender' | 'quoted' | 'forwarded'
> & {
  sender?: MinimalUser<SenderSpecifics>;
  quoted?: QuotedMessage<SenderSpecifics, Specifics>;
  forwarded?: ForwardedSchema<SenderSpecifics, Specifics>;
  specifics?: Specifics;
};

export type MessagePreview = Static<typeof messagePreviewSchema>;

export type QuotedMessage<SenderSpecifics = unknown, Specifics = unknown> = Omit<
  Static<typeof quotedMessageSchema>,
  'sender' | 'specifics'
> & {
  specifics?: Specifics;
  sender?: MinimalUser<SenderSpecifics>;
};

export type ForwardedSchema<SenderSpecifics = unknown, Specifics = unknown> = Omit<
  Static<typeof forwardedMessageSchema>,
  'sender' | 'specifics'
> & {
  specifics?: Specifics;
  sender?: MinimalUser<SenderSpecifics>;
};

export type MessageHyperlink = Static<typeof HyperlinkSchema>;

export type MessageMention = Static<typeof MentionSchema>;

export type ContentRelation = Static<typeof contentRelationSchema>;

export type MessageEventMetadata = Static<typeof metadataSchema>;
